import formatDistanceStrict from "date-fns/formatDistanceStrict";
import format from "date-fns/format";
import differenceInSeconds from "date-fns/differenceInSeconds";
import differenceInMonths from "date-fns/differenceInMonths";
import differenceInMinutes from "date-fns/differenceInMinutes";
import ptBR from "date-fns/locale/pt-BR";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IRelativeTimeProps extends React.HTMLAttributes<HTMLTimeElement> {
  timeStamp: number | Date;
  prefixWhenRecent?: string;
  prefixWhenDistant?: string;
  showFullDate?: boolean;
}

export default function RelativeTime({
  timeStamp,
  prefixWhenDistant,
  prefixWhenRecent,
  showFullDate,
  ...props
}: IRelativeTimeProps) {
  const intl = useIntl();

  const dateTime = new Date(timeStamp);
  const [, tick] = useState<number>(0);

  useEffect(() => {
    const distanceInMinutes = differenceInMinutes(new Date(), dateTime);

    let timeoutMs = 2 * 60; // 2 minutes

    if (distanceInMinutes > 60) {
      timeoutMs = 4 * 60; // every 4 minutes
    } else if (distanceInMinutes > 120) {
      timeoutMs = 6 * 60; // every 6 minutes
    } else if (distanceInMinutes <= 0) {
      timeoutMs = 30; // 10 seconds
    }

    const scheduledInterval = setInterval(() => tick(Date.now()), timeoutMs * 1000);

    return () => clearInterval(scheduledInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStamp]);

  const distanceInMonths = differenceInMonths(new Date(), dateTime);

  if (distanceInMonths > 3 || showFullDate) {
    const formattedDate = format(dateTime, "d 'de' MMMM, yyyy", {
      locale: ptBR,
    });
    return (
      <time
        {...props}
        suppressHydrationWarning={true}
        dateTime={dateTime.toISOString()}
        title={intl.formatDate(dateTime, {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        })}
      >
        {prefixWhenDistant ? `${prefixWhenDistant} ${formattedDate}` : formattedDate}
      </time>
    );
  }

  const distanceInSeconds = differenceInSeconds(new Date(), dateTime);
  const timeDistance = formatDistanceStrict(new Date(), dateTime, {
    locale: ptBR,
  });
  const formattedDate = distanceInSeconds < 30 ? "agora mesmo" : `há ${timeDistance}`;

  return (
    <time
      {...props}
      suppressHydrationWarning={true}
      dateTime={dateTime.toISOString()}
      title={intl.formatDate(dateTime, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      })}
    >
      {prefixWhenRecent ? `${prefixWhenRecent} ${formattedDate}` : formattedDate}
    </time>
  );
}
